import React, { useState } from 'react';
import { WizardStep } from './types/types';
import { WizardLayout } from './WizardLayout';
import SecurityIcon from '@material-ui/icons/Security';
import CredentialCreate from './steps/build';
import CredentialSign from './steps/CredentialSign';
import CredentialMask from './steps/mask';
import PresentationVerification from './steps/PresentationVerification';
import { trackEvent } from '../../utils/google-analytics';

const STEPS: WizardStep[] = [
    { label: 'Build', subLabel: 'Define credential' },
    { label: 'Sign', subLabel: 'Issuer signs' },
    { label: 'Present', subLabel: 'Holder presents' },
    { label: 'Verify', subLabel: 'Verifier checks' }
];

const STEP_CONTENT = [
    {
        title: 'Build the credential',
        description:
            'Define the Verifiable Credential by defining claims, credential types, issuer details, and subject information before signing.'
    },
    {
        title: 'Sign the credential',
        description:
            'Generate a signed credential using the issuer’s private key and the holder’s public key.'
    },
    {
        title: 'Mask the credential',
        description:
            'Control data privacy by revealing only selected claims while securely masking the remaining credential fields.'
    },
    {
        title: 'Verify the presentation',
        description:
            'Validate the presentation signature and ensure the disclosed credential data has not been modified.'
    }
];

const WizardUI: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [credentialBuilt, setCredentialBuilt] = useState(false);
    const [credentialSigned, setCredentialSigned] = useState(false);
    const [credentialMasked, setCredentialMasked] = useState(false);

    const handleNext = () => {
        const next = Math.min(activeStep + 1, STEPS.length - 1);
        trackEvent('Credential Studio', 'wizard_step_advance', STEPS[next].label);
        setActiveStep(next);
    };

    const handleBack = () => {
        const prev = Math.max(activeStep - 1, 0);
        trackEvent('Credential Studio', 'wizard_step_back', STEPS[prev].label);
        setActiveStep(prev);
    };

    return (
        <WizardLayout
            steps={STEPS}
            activeStep={activeStep}
            onStepClick={setActiveStep}
            brandIcon={<SecurityIcon style={{ color: '#ffffff' }} />}
            brandName="SD Credential Studio"
            stepIndicator={`Step ${activeStep + 1} of ${STEPS.length}`}
            title={STEP_CONTENT[activeStep].title}
            description={STEP_CONTENT[activeStep].description}
            onBack={activeStep > 0 ? handleBack : undefined}
            onNext={
                activeStep < STEPS.length - 1
                    ? (activeStep === 0 && !credentialBuilt) ||
                      (activeStep === 1 && !credentialSigned) ||
                      (activeStep === 2 && !credentialMasked)
                        ? undefined
                        : handleNext
                    : undefined
            }
            nextLabel={
                activeStep < STEPS.length - 1
                    ? `Continue to ${STEPS[activeStep + 1].label}`
                    : 'Finish'
            }>
            <div style={{ display: activeStep === 0 ? undefined : 'none' }}>
                <CredentialCreate
                    onGenerate={() => {
                        setCredentialBuilt(true);
                        trackEvent('Credential Studio', 'credential_built');
                    }}
                />
            </div>

            <div style={{ display: activeStep === 1 ? undefined : 'none' }}>
                <CredentialSign
                    onSign={() => {
                        setCredentialSigned(true);
                        trackEvent('Credential Studio', 'credential_signed');
                    }}
                />
            </div>

            <div style={{ display: activeStep === 2 ? undefined : 'none' }}>
                <CredentialMask
                    onMask={() => {
                        setCredentialMasked(true);
                        trackEvent('Credential Studio', 'credential_masked');
                    }}
                />
            </div>

            <div style={{ display: activeStep === 3 ? undefined : 'none' }}>
                <PresentationVerification />
            </div>
        </WizardLayout>
    );
};

export default WizardUI;
